import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Button } from 'react-bootstrap';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import { useAuth } from '../context/AuthContext';
import './ImpersonationBanner.css';

const ImpersonationBanner = ({ user: userProp }) => {
  const { user: authUser } = useAuth();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);
  const isCheckingRef = useRef(false);
  
  // Use prop user if provided, otherwise use auth context user
  const user = userProp || authUser;

  // Check impersonation status directly
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const checkStatus = async () => {
      // Prevent multiple simultaneous requests
      if (isCheckingRef.current) {
        return;
      }

      isCheckingRef.current = true;
      try {
        const response = await axios.get(createApiUrl('/impersonation/status'), {
          withCredentials: true
        });
        
        if (response.data.success) {
          // Check multiple sources for impersonation status
          const responseIsImpersonating = response.data.isImpersonating;
          const userIsImpersonated = response.data.user && response.data.user.isImpersonated;
          
          const isCurrentlyImpersonating = responseIsImpersonating === true || 
                                           userIsImpersonated === true ||
                                           !!responseIsImpersonating ||
                                           !!userIsImpersonated;
          
          // Only update state if it actually changed
          setIsImpersonating(prev => {
            if (prev !== isCurrentlyImpersonating) {
              return isCurrentlyImpersonating;
            }
            return prev;
          });
          
          // Only update impersonationData if it actually changed
          setImpersonationData(prev => {
            const newData = response.data;
            // Simple comparison - only update if key values changed
            if (!prev || 
                prev.isImpersonating !== newData.isImpersonating ||
                prev.user?.email !== newData.user?.email ||
                prev.originalUser?.email !== newData.originalUser?.email) {
              return newData;
            }
            return prev;
          });
        }
      } catch (error) {
        // Handle 401 (Unauthorized) gracefully - user is not authenticated or session expired
        if (error.response?.status === 401) {
          // User is not authenticated, so not impersonating
          setIsImpersonating(false);
          setImpersonationData(null);
          // Stop the interval if we get 401 - user is not authenticated
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }
        // Only log other errors (not 401)
        if (error.response?.status !== 401) {
          console.error('[ImpersonationBanner] Error checking status:', error);
        }
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Only check if user is available (authenticated)
    if (user) {
      checkStatus();
      // Check less frequently to avoid loops - only poll every 10 seconds
      intervalRef.current = setInterval(checkStatus, 10000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isCheckingRef.current = false;
    };
  }, [user]);

  const stopImpersonation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(createApiUrl('/impersonation/stop'), {}, {
        withCredentials: true
      });

      if (response.data.success) {
        setIsImpersonating(false);
        setImpersonationData(null);
        window.location.href = '/admin-dashboard';
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.message || 'Failed to stop impersonation');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to stop impersonation';
      alert(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Memoize shouldShow calculation to prevent unnecessary re-renders
  const shouldShow = useMemo(() => {
    return isImpersonating === true || 
           isImpersonating === 1 ||
           user?.isImpersonated === true ||
           user?.isImpersonated === 1 ||
           impersonationData?.isImpersonating === true ||
           impersonationData?.isImpersonating === 1 ||
           (impersonationData?.user && (impersonationData.user.isImpersonated === true || impersonationData.user.isImpersonated === 1));
  }, [isImpersonating, user?.isImpersonated, impersonationData?.isImpersonating, impersonationData?.user]);
  
  // Memoize displayUser to prevent unnecessary recalculations
  const displayUser = useMemo(() => {
    return user || impersonationData?.user;
  }, [user, impersonationData?.user]);

  // Memoize display name calculation
  const displayName = useMemo(() => {
    if (!displayUser) return 'User';
    if (displayUser.contactName) return displayUser.contactName;
    if (displayUser.firstName && displayUser.lastName) return `${displayUser.firstName} ${displayUser.lastName}`;
    if (displayUser.name) return displayUser.name;
    return displayUser.email || displayUser.username || 'User';
  }, [displayUser]);

  // Memoize original admin name calculation
  const originalAdminName = useMemo(() => {
    if (impersonationData?.originalUser) {
      return impersonationData.originalUser.username || 
             impersonationData.originalUser.email || 
             'Admin';
    }
    return displayUser?.impersonatedByUsername || 'Admin';
  }, [impersonationData?.originalUser, displayUser?.impersonatedByUsername]);

  // Memoize stop impersonation handler
  const handleReturnToAdmin = useCallback(async () => {
    await stopImpersonation();
  }, []);

  if (!shouldShow || !displayUser) {
    return null;
  }

  return (
    <div className="impersonation-banner" style={{ zIndex: 1000, position: 'relative' }}>
      <div className="impersonation-banner-content">
        <div className="impersonation-banner-icon">
          <i className="fas fa-building"></i>
        </div>
        <div className="impersonation-banner-text">
          <strong>Impersonating:</strong> {displayName} 
          {displayUser?.schoolCode && ` - School Code: ${displayUser.schoolCode}`}
          <br />
          <small>(Original Admin: {originalAdminName})</small>
        </div>
        <Button 
          className="impersonation-return-btn"
          onClick={handleReturnToAdmin}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Returning...
            </>
          ) : (
            <>
              <i className="fas fa-arrow-left me-2"></i>
              Return to Admin
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders when props haven't changed
export default React.memo(ImpersonationBanner);
